import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { useAuth } from "@core/contexts/AuthContext";
import { useSoloTrial } from "@core/hooks/useSoloTrial";
import { UsageAnalyticsTracker } from "@core/services/UsageAnalyticsTracker";
import {
  TRIAL_SNOOZE_CHOICES,
  type TrialSnoozeChoice,
} from "@core/constants/quotas";

/**
 * Bandeau d'essai du tableau de bord.
 *
 * Sans lui l'essai est invisible : les murs payants disparaissent tout seuls
 * pendant l'essai (ils dépendent de `isPaidPlanType`, vrai pendant l'essai),
 * donc l'utilisateur constate que les groupes marchent sans jamais apprendre
 * que c'est temporaire. Le bandeau est le seul endroit qui le dit.
 *
 * TROIS RÉPONSES, PAS UNE. Jusqu'ici la proposition ne pouvait recevoir que
 * « oui » (cliquer) ou « ignorer » — et le bandeau revenait indéfiniment, ce
 * qui n'est pas une réponse mais une absence de réponse. S'ajoutent « non
 * merci » et « plus tard ».
 *
 * LA QUESTION POSÉE DERRIÈRE « PLUS TARD » EST LE POINT QUI DÉCIDE DE LA
 * QUALITÉ DE LA RÉPONSE. Ce n'est pas « quand veux-tu qu'on te le
 * repropose ? », qui demande à la personne de prédire son intérêt pour notre
 * produit ; c'est « tu organises quelque chose bientôt ? », à laquelle elle
 * répond depuis son propre calendrier. Même mécanique, mais on obtient une
 * date réelle au lieu d'une politesse — et, accessoirement, le signal produit
 * le plus précieux de tout l'entonnoir : une intention déclarée d'organiser
 * quelque chose à une date connue.
 *
 * Volontairement non fermable pendant l'essai : c'est un compte à rebours,
 * pas une publicité. Le masquer reviendrait à cacher l'information qui permet
 * de décider avant la fin.
 */
export const TrialBanner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    isActive,
    daysRemaining,
    hasUsed,
    offerPending,
    snoozeElapsed,
    responding,
    declineOffer,
    snoozeOffer,
  } = useSoloTrial();
  const [panneauReport, setPanneauReport] = useState(false);
  const [rappel, setRappel] = useState(false);

  const trackAndNavigate = (to: string) => {
    void UsageAnalyticsTracker.track("upgrade_cta_clicked", {
      source: "banner",
    });
    navigate(to);
  };

  if (!user) return null;

  if (isActive && daysRemaining !== null) {
    return (
      <Bandeau
        ton="actif"
        texte={t("trial_banner.active", { count: daysRemaining })}
        ctaLabel={t("trial_banner.active_cta")}
        onCta={() => trackAndNavigate("/pricing")}
        testId="trial-banner-active"
      />
    );
  }

  if (offerPending) {
    if (panneauReport) {
      return (
        <PanneauReport
          responding={responding}
          rappel={rappel}
          onRappel={setRappel}
          onChoisir={async (choix) => {
            const ok = await snoozeOffer(choix, rappel);
            if (ok) setPanneauReport(false);
          }}
          onAnnuler={() => setPanneauReport(false)}
        />
      );
    }
    return (
      <Bandeau
        ton="offre"
        // Report échu : un AUTRE texte, ancré sur ce que la personne a dit.
        // Reservir le message initial donnerait l'impression que sa réponse
        // n'a pas été entendue.
        texte={
          snoozeElapsed
            ? t("trial_banner.snooze_elapsed")
            : t("trial_banner.available")
        }
        ctaLabel={t("trial_banner.available_cta")}
        onCta={() => trackAndNavigate("/essai")}
        testId={
          snoozeElapsed
            ? "trial-banner-snooze-elapsed"
            : "trial-banner-available"
        }
        secondaires={
          <>
            <BoutonDiscret
              onClick={() => setPanneauReport(true)}
              disabled={responding}
              testId="trial-banner-later"
            >
              {t("trial_banner.later")}
            </BoutonDiscret>
            <BoutonDiscret
              onClick={() => void declineOffer()}
              disabled={responding}
              testId="trial-banner-decline"
            >
              {t("trial_banner.decline")}
            </BoutonDiscret>
          </>
        }
      />
    );
  }

  // Essai consommé et terminé : on ne repropose pas ce qui n'existe plus.
  if (hasUsed) {
    return (
      <Bandeau
        ton="offre"
        texte={t("trial_banner.ended")}
        ctaLabel={t("trial_banner.ended_cta")}
        onCta={() => trackAndNavigate("/pricing")}
        testId="trial-banner-ended"
      />
    );
  }

  return null;
};

const PanneauReport: React.FC<{
  responding: boolean;
  rappel: boolean;
  onRappel: (v: boolean) => void;
  onChoisir: (choix: TrialSnoozeChoice) => void;
  onAnnuler: () => void;
}> = ({ responding, rappel, onRappel, onChoisir, onAnnuler }) => {
  const { t } = useTranslation();
  return (
    <div
      data-testid="trial-banner-snooze-panel"
      className="mb-6 rounded-xl border border-border bg-card px-4 py-3"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-foreground">
          {t("trial_banner.snooze_question")}
        </p>
        <button
          type="button"
          onClick={onAnnuler}
          aria-label={t("trial_banner.snooze_cancel")}
          data-testid="trial-banner-snooze-cancel"
          className="flex-shrink-0 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {TRIAL_SNOOZE_CHOICES.map((choix) => (
          <button
            key={choix.id}
            type="button"
            disabled={responding}
            onClick={() => onChoisir(choix.id)}
            data-testid={`trial-banner-snooze-${choix.id}`}
            className="rounded-lg border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted disabled:opacity-50"
          >
            {t(`trial_banner.snooze_choice.${choix.id}`)}
          </button>
        ))}
      </div>
      {/*
        Case NON pré-cochée, et son refus n'annule pas le report : ce sont
        deux réponses distinctes. Le consentement porte ici sur un rappel que
        la personne demande, à une date qu'elle choisit — pas sur de la
        prospection, qui a sa propre case sur l'écran /essai.
      */}
      <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={rappel}
          onChange={(e) => onRappel(e.target.checked)}
          data-testid="trial-banner-snooze-remind"
          className="h-3.5 w-3.5 rounded border-border"
        />
        {t("trial_banner.snooze_remind")}
      </label>
    </div>
  );
};

const BoutonDiscret: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  testId: string;
  children: React.ReactNode;
}> = ({ onClick, disabled, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className="flex-shrink-0 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
  >
    {children}
  </button>
);

const Bandeau: React.FC<{
  ton: "actif" | "offre";
  texte: string;
  ctaLabel: string;
  onCta: () => void;
  testId: string;
  secondaires?: React.ReactNode;
}> = ({ ton, texte, ctaLabel, onCta, testId, secondaires }) => (
  <div
    data-testid={testId}
    className={
      ton === "actif"
        ? "mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3"
        : "mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3"
    }
  >
    <span className="flex min-w-0 items-center gap-2">
      <Sparkles
        className="h-4 w-4 flex-shrink-0 text-primary"
        aria-hidden="true"
      />
      <span className="text-sm text-foreground">{texte}</span>
    </span>
    <span className="flex flex-shrink-0 flex-wrap items-center gap-1">
      {secondaires}
      <button
        type="button"
        onClick={onCta}
        className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
      >
        {ctaLabel}
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </span>
  </div>
);
